import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ClientSideRowModelModule, ModuleRegistry } from 'ag-grid-community';
import type { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import DateInput from './DateInput';
import StockChart from './StockChart';
import type { TradeMarker } from './StockChart';
import { getMainAxios } from '../utils/util';
import { callAxiosGet } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { useAuth } from '../context/AuthContext';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

/**
 *  체결 현황분석
 *
 *  기간으로 체결을 모아 **일자 → 시간대 → 종목** 순으로 좁혀 본다.
 *  기존 화면(포트폴리오·복기)은 그대로 두고 분석 전용으로 따로 만들었다.
 *
 *  구성:
 *    1) 상단 — 일자별 체결현황 (기본 오늘자). 행 클릭 시 아래가 그날로 바뀐다
 *    2) 중간 — 시간대별(30분) 체결·손익 + 종목별 손익
 *    3) 하단 — 체결 차트: 체결 시점·가격을 찍고 누적손익 곡선을 겹친다
 *
 *  손익은 다른 화면과 **같은 식**이다 (매도대금 − 매수대금, 비용 전).
 *  식을 새로 만들면 화면마다 숫자가 갈린다.
 */

interface DailyRow {
  htsId: string; pfId: number; d: string;
  nFill: number; nSym: number; nBuy: number; nSell: number;
  buyAmt: number; sellAmt: number; buyQty: number; sellQty: number;
  /** 현금흐름(매도−매수). 참고용 — 매수만 한 날은 음수다 */
  cashFlow: number;
  /** 보유분 평가손익 (현재가 − 평단) × 보유 */
  unrealized: number;
  /** 총손익 = 실현 + 평가 */
  pnl: number; pnlRt: number; flat: boolean;
  firstT: string; lastT: string;
  fee: number; tax: number; cost: number; netPnl: number;
}

interface TimeRow {
  t: string; nFill: number; nBuy: number; nSell: number;
  buyAmt: number; sellAmt: number; buyQty: number; sellQty: number;
  /** 그 구간 현금흐름 (매도 − 매수). 매수만 한 구간은 음수 — 손실이 아니다 */
  cashFlow: number;
  /** 매도한 만큼만 잡은 실현손익. 매수만 한 구간은 0 */
  realized: number;
  fee: number; tax: number; cost: number; netRealized: number;
}

interface SymRow {
  prodId: string; prodNm?: string; nFill: number;
  bQty: number; sQty: number; bAmt: number; sAmt: number;
  bAvg: number; sAvg: number;
  cashFlow: number;
  /** 판 만큼의 손익 */
  realized: number;
  /** 보유분 평가손익 */
  unrealized: number;
  /** 총손익 = realized + unrealized */
  pnl: number; pnlRt: number;
  heldQty: number; heldAvg: number; curPrc: number;
  /** KIS 값 (대조용) */
  kisHeld: number; kisAvg: number; kisUnreal: number;
  /** 우리 − KIS. 0 이면 양쪽이 같은 결론 */
  unrealGap: number; heldGap: number;
  flat: boolean;
  fee: number; tax: number; cost: number; netPnl: number;
}

interface FillRow {
  t: string; ordNo: number; prodId: string; prodNm?: string;
  isBuy: boolean; lsNm: string; qty: number; prc: number; amt: number; flow: number;
  cost: number; realized: number;
}

const numFmt = (p: any) => (p.value == null ? '-' : Math.round(Number(p.value)).toLocaleString());
const prcFmt = (p: any) => (p.value ? Number(p.value).toFixed(2) : '-');
const pctFmt = (p: any) => (p.value == null ? '-' : `${Number(p.value).toFixed(2)}%`);
/** 손익 색 — 번 날 초록, 잃은 날 빨강 (다른 화면과 같은 규칙) */
const sign = (v?: number | null) => ((v ?? 0) >= 0 ? '#22c55e' : '#ef4444');

const card: React.CSSProperties = {
  backgroundColor: 'white', border: '2px solid #3b82f6',
  borderRadius: '8px', padding: '16px', marginBottom: '16px',
};
const titleRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between',
  alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: 8,
};
const gridTitle: React.CSSProperties = { fontSize: '13px', fontWeight: 600, color: '#1e40af', margin: 0 };
const gridHint: React.CSSProperties = { fontSize: '11px', fontWeight: 400, color: '#9ca3af' };

const FillAnalysis: React.FC = () => {
  const axiosInstance = useMemo(() => getMainAxios(), []);
  const { msgBox: m, logger } = useMessageState();
  const { selectedPfId, portfolios } = useAuth();

  const today = new Date().toISOString().slice(0, 10);
  // 기본은 **오늘 하루**. 기간을 넓히려면 시작일만 바꾸면 된다.
  const [d0, setD0] = useState(today);
  const [d1, setD1] = useState(today);
  const [pfId, setPfId] = useState<number | null>(selectedPfId ?? null);

  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [selD, setSelD] = useState<string | null>(null);
  const [byTime, setByTime] = useState<TimeRow[]>([]);
  const [bySym, setBySym] = useState<SymRow[]>([]);
  const [fills, setFills] = useState<FillRow[]>([]);
  const [loading, setLoading] = useState(false);
  // 비용이 KIS 하루합계를 안분한 값인지 (체결에 실제 값이 없을 때)
  const [costFromKis, setCostFromKis] = useState(false);
  // 손익 합계 — 서버가 계산해 내려준다 (화면에서 더하면 표와 어긋난다)
  const [sums, setSums] = useState<{
    realized: number; unrealized: number; pnl: number; cost: number; net: number;
    kisUnreal: number; unrealGap: number;
  } | null>(null);
  // 분봉차트에 띄울 종목. 하루를 고르면 거래가 가장 큰 종목으로 자동 선택된다.
  const [chartSym, setChartSym] = useState<string | null>(null);

  useEffect(() => { if (selectedPfId != null) setPfId(selectedPfId); }, [selectedPfId]);

  const fetchDay = useCallback((d: string, pf: number | null) => {
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/pfbs/fill-analysis-day',
      params: { pfId: pf ?? undefined, d },
      onSuccess: (data) => {
        const r = data as any;
        setByTime(r?.byTime ?? []);
        setBySym(r?.bySym ?? []);
        setFills(r?.fills ?? []);
        setCostFromKis(!!r?.costFromKis);
        setSums({
          realized: r?.sumRealized ?? 0, unrealized: r?.sumUnrealized ?? 0,
          pnl: r?.sumPnl ?? 0, cost: r?.sumCost ?? 0, net: r?.sumNetPnl ?? 0,
          kisUnreal: r?.sumKisUnreal ?? 0, unrealGap: r?.sumUnrealGap ?? 0,
        });
        setSelD(d);
        // 그날 손익이 가장 큰 종목을 띄운다 (bySym 은 |손익| 내림차순이다)
        setChartSym((r?.bySym ?? [])[0]?.prodId ?? null);
      },
      onError: () => { setByTime([]); setBySym([]); setFills([]); setSums(null); setSelD(d); },
    });
  }, [axiosInstance, m, logger]);

  const fetchAll = useCallback(() => {
    setLoading(true);
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/pfbs/fill-analysis',
      params: { pfId: pfId ?? undefined, startDate: d0, endDate: d1 },
      onSuccess: (data) => {
        const rows = ((data as any)?.daily ?? []) as DailyRow[];
        setDaily(rows);
        setLoading(false);
        // 조회 직후 **가장 최근 일자**를 자동으로 펼친다 — 한 번 더 클릭하게 하지 않는다.
        if (rows.length > 0) fetchDay(rows[0].d, pfId);
        else { setByTime([]); setBySym([]); setFills([]); setSelD(null); }
      },
      onError: () => {
        setDaily([]); setByTime([]); setBySym([]); setFills([]); setSelD(null);
        setLoading(false);
      },
    });
  }, [axiosInstance, m, logger, pfId, d0, d1, fetchDay]);

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [pfId]);

  // ── 컬럼 ────────────────────────────────────────────────────────
  const dailyCols = useMemo<ColDef<DailyRow>[]>(() => [
    { field: 'd', headerName: '일자', width: 110, cellStyle: { textAlign: 'center' } },
    { field: 'htsId', headerName: 'HTS', width: 85, cellStyle: { textAlign: 'center' } },
    { field: 'nFill', headerName: '체결', width: 70, cellStyle: { textAlign: 'right' } },
    { field: 'nBuy', headerName: '매수', width: 70, cellStyle: { textAlign: 'right', color: '#ef4444' } },
    { field: 'nSell', headerName: '매도', width: 70, cellStyle: { textAlign: 'right', color: '#2563eb' } },
    { field: 'nSym', headerName: '종목', width: 70, cellStyle: { textAlign: 'right' } },
    { field: 'buyAmt', headerName: '매수대금', width: 115, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'sellAmt', headerName: '매도대금', width: 115, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      field: 'unrealized', headerName: '평가손익', width: 100,
      headerTooltip: '보유분 (현재가 − 평단) × 수량. 그날 안 판 물량의 손익',
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }),
      valueFormatter: numFmt,
    },
    {
      // 증권사 표준: 총손익 = 실현 + 평가 (키움 [0356] · 한투 [2100])
      field: 'pnl', headerName: '손익', width: 105,
      headerTooltip: '총손익 = 실현손익 + 평가손익(보유분 현재가 기준). 비용 전',
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }),
      valueFormatter: numFmt,
    },
    {
      field: 'cost', headerName: '수수료+세금', width: 105,
      headerTooltip: 'KIS 는 체결별 비용을 주지 않는다 — 잔고(kispfbs)의 하루 합계다',
      cellStyle: { textAlign: 'right', color: '#6b7280' }, valueFormatter: numFmt,
    },
    {
      field: 'netPnl', headerName: '순손익', width: 110,
      headerTooltip: '손익 − 비용. **그날 실제로 번 돈**',
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 700, color: sign(p.value) }),
      valueFormatter: numFmt,
    },
    {
      field: 'pnlRt', headerName: '수익률(%)', width: 95,
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 600, color: sign(p.value) }),
      valueFormatter: pctFmt,
    },
    {
      field: 'flat', headerName: '청산', width: 80,
      headerTooltip: '매수수량 == 매도수량. 미청산이면 손익이 일부만 실현된 것이다',
      cellStyle: { textAlign: 'center' },
      valueFormatter: (p) => (p.value ? '완료' : '미청산'),
    },
    { field: 'firstT', headerName: '첫 체결', width: 90, cellStyle: { textAlign: 'center', color: '#6b7280' } },
    { field: 'lastT', headerName: '마지막', width: 90, cellStyle: { textAlign: 'center', color: '#6b7280' } },
  ], []);

  const timeCols = useMemo<ColDef<TimeRow>[]>(() => [
    { field: 't', headerName: '시간대', width: 90, cellStyle: { textAlign: 'center', fontWeight: 600 } },
    { field: 'nFill', headerName: '체결', width: 70, cellStyle: { textAlign: 'right' } },
    { field: 'nBuy', headerName: '매수', width: 70, cellStyle: { textAlign: 'right', color: '#ef4444' } },
    { field: 'nSell', headerName: '매도', width: 70, cellStyle: { textAlign: 'right', color: '#2563eb' } },
    { field: 'buyQty', headerName: '매수수량', width: 95, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'sellQty', headerName: '매도수량', width: 95, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'buyAmt', headerName: '매수대금', width: 110, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'sellAmt', headerName: '매도대금', width: 110, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    {
      // 매수만 한 구간이 '손실' 로 보이던 것을 고쳤다 (2026-10-11).
      // 현금흐름과 실현손익은 다른 것이다 — 나눠서 보여준다.
      field: 'cashFlow', headerName: '현금흐름', width: 110,
      headerTooltip: '매도대금 − 매수대금. 매수만 한 구간은 음수다 — '
                   + '현금이 나간 것이지 손실이 아니다',
      cellStyle: (p) => ({ textAlign: 'right', color: '#64748b' }),
      valueFormatter: numFmt,
    },
    {
      field: 'realized', headerName: '실현손익', width: 110,
      headerTooltip: '매도한 만큼만 잡은 손익 ((체결가 − 평단) × 수량). '
                   + '매수만 한 구간은 0 이고, 매도가 나와야 숫자가 생긴다',
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 700, color: sign(p.value) }),
      valueFormatter: numFmt,
    },
    {
      field: 'cost', headerName: '수수료+세금', width: 105,
      headerTooltip: 'KIS 하루 합계를 체결금액 비율로 안분한 값 (체결별 비용을 안 준다)',
      cellStyle: { textAlign: 'right', color: '#6b7280' }, valueFormatter: numFmt,
    },
    {
      field: 'netRealized', headerName: '순실현손익', width: 110,
      headerTooltip: '실현손익 − 비용',
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 600, color: sign(p.value) }),
      valueFormatter: numFmt,
    },
  ], []);

  const symCols = useMemo<ColDef<SymRow>[]>(() => [
    { field: 'prodId', headerName: '종목코드', width: 95, cellStyle: { textAlign: 'center' } },
    { field: 'prodNm', headerName: '종목명', minWidth: 150, flex: 1 },
    { field: 'nFill', headerName: '체결', width: 70, cellStyle: { textAlign: 'right' } },
    { field: 'bQty', headerName: '매수수량', width: 95, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'bAvg', headerName: '매수평단', width: 95, cellStyle: { textAlign: 'right' }, valueFormatter: prcFmt },
    { field: 'sQty', headerName: '매도수량', width: 95, cellStyle: { textAlign: 'right' }, valueFormatter: numFmt },
    { field: 'sAvg', headerName: '매도평단', width: 95, cellStyle: { textAlign: 'right' }, valueFormatter: prcFmt },
    {
      field: 'heldQty', headerName: '보유', width: 80,
      headerTooltip: '그날 안 판 수량. 0 이면 전량청산',
      cellStyle: { textAlign: 'right', color: '#6b7280' }, valueFormatter: numFmt,
    },
    {
      field: 'realized', headerName: '실현손익', width: 105,
      headerTooltip: '판 만큼의 손익 ((매도가 − 평단) × 매도수량)',
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }), valueFormatter: numFmt,
    },
    {
      field: 'unrealized', headerName: '평가손익', width: 105,
      headerTooltip: '**우리가 계산한** 보유분 손익 — (현재가 − 우리평단) × 우리보유. '
                   + '평단·보유는 체결에서 직접 쌓고 현재가만 KIS 것을 쓴다',
      cellStyle: (p) => ({ textAlign: 'right', color: sign(p.value) }), valueFormatter: numFmt,
    },
    {
      field: 'kisUnreal', headerName: 'KIS 평가손익', width: 110,
      headerTooltip: 'KIS 가 준 평가손익(kispfpos.prft). 위 자체계산과 대조용',
      cellStyle: { textAlign: 'right', color: '#64748b' }, valueFormatter: numFmt,
    },
    {
      // 검증 핵심 — 두 경로가 같은 값에 닿았는지
      field: 'unrealGap', headerName: '평가 차이', width: 95,
      headerTooltip: '자체계산 − KIS. 0 이면 평단·보유 계산이 KIS 와 일치한다',
      valueFormatter: (p) => {
        if (p.value == null) return '-';
        const v = Math.round(Number(p.value));
        return v === 0 ? '0 ✓' : v.toLocaleString();
      },
      cellStyle: (p) => {
        const ok = Math.abs(Number(p.value ?? 0)) < 1;
        return {
          textAlign: 'right', fontWeight: 700,
          color: ok ? '#16a34a' : '#b45309',
          backgroundColor: ok ? '#f0fdf4' : '#fffbeb',
        };
      },
    },
    {
      // 증권사 표준: 총손익 = 실현 + 평가 (키움 [0356] · 한투 [2100])
      field: 'pnl', headerName: '총손익', width: 110,
      headerTooltip: '실현손익 + 평가손익',
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 700, color: sign(p.value) }),
      valueFormatter: numFmt,
    },
    {
      field: 'cost', headerName: '수수료+세금', width: 105,
      headerTooltip: 'KIS 하루 합계를 체결금액 비율로 안분한 값',
      cellStyle: { textAlign: 'right', color: '#6b7280' }, valueFormatter: numFmt,
    },
    {
      field: 'netPnl', headerName: '순손익', width: 110,
      headerTooltip: '손익 − 비용',
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 700, color: sign(p.value) }),
      valueFormatter: numFmt,
    },
    {
      field: 'pnlRt', headerName: '수익률(%)', width: 95,
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 600, color: sign(p.value) }),
      valueFormatter: pctFmt,
    },
    {
      field: 'flat', headerName: '청산', width: 80,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (p) => (p.value ? '완료' : '미청산'),
    },
  ], []);

  // ── 체결 차트 ────────────────────────────────────────────────────
  //
  // x 축을 '시간' 이 아니라 **체결 순번**으로 둔다. 체결은 하루에 몇 건뿐이라
  // 시간축에 놓으면 점이 한쪽에 뭉쳐 읽을 수가 없다 (실측 10-07: 09시 11건·15시 2건).
  // 순번으로 깔면 간격이 고르고, 실제 시각은 라벨·툴팁으로 보여준다.
  /**
   * 분봉차트에 올릴 매수·매도 표식 — **선택한 종목의 체결만** 넘긴다.
   * StockChart 는 한 종목의 분봉을 그리므로 다른 종목 체결을 섞으면
   * 가격축이 안 맞아 엉뚱한 자리에 찍힌다.
   */
  const candleMarkers = useMemo<TradeMarker[]>(() => {
    if (!chartSym || !selD) return [];
    return fills
      .filter((f) => f.prodId === chartSym && f.prc > 0)
      .map((f) => ({
        t: `${selD} ${f.t}`,          // StockChart 는 "yyyy-MM-dd HH:mm:ss" 를 받는다
        prc: f.prc,
        qty: f.qty,
        isBuy: f.isBuy,
        isFill: true,                  // 여기 데이터는 전부 체결이다
        note: f.realized ? `실현손익 ${Math.round(f.realized).toLocaleString()}원` : undefined,
      }));
  }, [fills, chartSym, selD]);

  const chartOptions = useMemo<Highcharts.Options>(() => {
    const cats = fills.map((f) => f.t);

    // ── 가격을 **종목별 등락률(%)** 로 바꿔 그린다 (2026-10-11) ──────────────
    //
    // 예전에는 체결가(원)를 그대로 y 축에 올렸다. 그런데 한 화면에 가격대가
    // 다른 종목이 섞이면 **서로 뭉개져 아무것도 안 보인다.**
    //   실측 10-07: 114800 은 982~1,005원, 251340 은 2,130~2,177원
    //   → 축이 900~2,400 으로 잡혀 114800 의 23원 움직임이 直線처럼 눌린다.
    //
    // 그래서 **그날 그 종목의 첫 체결가를 0% 기준**으로 잡고 등락률로 환산한다.
    // 가격대가 달라도 같은 축에서 비교되고, "얼마에 사서 얼마에 팔았나" 가 보인다.
    // 원래 가격은 툴팁에 그대로 남긴다.
    const basePrc = new Map<string, number>();
    fills.forEach((f) => {
      if (!basePrc.has(f.prodId) && f.prc > 0) basePrc.set(f.prodId, f.prc);
    });
    const pctOf = (f: FillRow) => {
      const b = basePrc.get(f.prodId);
      return b && b > 0 ? ((f.prc - b) / b) * 100 : 0;
    };

    // 종목이 하나면 등락률로 바꿀 이유가 없다 — 원래 가격이 더 직관적이다.
    const symCnt = basePrc.size;
    const usePct = symCnt > 1;
    const yOf = (f: FillRow) => (usePct ? pctOf(f) : f.prc);

    const buy = fills.map((f, i) => (f.isBuy ? { x: i, y: yOf(f), custom: f } : null)).filter(Boolean) as any[];
    const sell = fills.map((f, i) => (!f.isBuy ? { x: i, y: yOf(f), custom: f } : null)).filter(Boolean) as any[];

    // 종목별 가격 선 — 체결 사이를 이어 그 종목이 어떻게 움직였는지 보인다.
    // 점만 찍혀 있으면 어느 점이 같은 종목인지 알 수 없었다.
    // 가격선 색 — **체결 표식(초록▲·주황▼)과 겹치지 않는 색**만 쓴다.
    // 처음엔 teal·green 을 넣었다가 매수 표식과 같은 색으로 보여
    // "매수가 지그재그 선으로 그려진다" 는 오해를 만들었다 (2026-10-11 실측).
    const symColors = ['#64748b', '#a855f7', '#0ea5e9', '#f59e0b', '#ec4899'];
    const symSeries = [...basePrc.keys()].map((sym, si) => {
      const nm = fills.find((f) => f.prodId === sym)?.prodNm ?? sym;
      return {
        type: 'line',
        name: `${nm}`,
        data: fills.map((f, i) => (f.prodId === sym ? { x: i, y: yOf(f), custom: f } : null))
                   .filter(Boolean) as any[],
        yAxis: 0,
        color: symColors[si % symColors.length],
        lineWidth: 1,
        dashStyle: 'ShortDot',
        marker: { enabled: false },
        enableMouseTracking: false,   // 툴팁은 매수/매도 표식이 맡는다
      } as Highcharts.SeriesLineOptions;
    });

    // 누적 손익 — 매수(−)/매도(+) 현금흐름을 순서대로 쌓는다.
    // 마지막 값이 그날 손익(비용 전)과 같아야 한다.
    let acc = 0;
    const cum = fills.map((f, i) => { acc += f.flow; return { x: i, y: Math.round(acc) }; });

    return {
      chart: { height: 420, zooming: { type: undefined }, panning: { enabled: false } },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: {
        categories: cats,
        labels: { style: { fontSize: '10px' }, step: Math.max(1, Math.ceil(cats.length / 14)) },
        tickmarkPlacement: 'on',
      },
      yAxis: [
        {
          title: { text: usePct ? '등락률 (첫 체결 대비 %)' : '체결가', style: { fontSize: '11px' } },
          labels: {
            style: { fontSize: '10px' },
            formatter(this: any) {
              return usePct ? `${Number(this.value).toFixed(2)}%` : Number(this.value).toLocaleString();
            },
          },
          // 0% 선 — 본전 자리. 이 위에서 팔면 이익이다.
          plotLines: usePct ? [{ value: 0, color: '#cbd5e1', width: 1 }] : [],
        },
        {
          title: { text: '누적손익', style: { fontSize: '11px' } },
          labels: { style: { fontSize: '10px' } },
          opposite: true,
          plotLines: [{ value: 0, color: '#94a3b8', width: 1, dashStyle: 'Dash' }],
        },
      ],
      legend: { enabled: true, itemStyle: { fontSize: '11px' } },
      tooltip: {
        shared: false,
        formatter(this: any) {
          const c = this.point?.custom as FillRow | undefined;
          if (!c) {
            return `<b>${cats[this.point.x] ?? ''}</b><br/>누적손익 ${Number(this.y).toLocaleString()}원`;
          }
          const pct = pctOf(c);
          const rz = Math.round(c.realized ?? 0);
          return `<b>${c.t}</b> ${c.prodNm ?? c.prodId}<br/>`
            + `${c.isBuy ? '매수' : '매도'} ${c.qty.toLocaleString()}주 @ ${c.prc.toLocaleString()}원`
            + (usePct ? ` (${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%)` : '') + '<br/>'
            + `금액 ${Math.round(c.amt).toLocaleString()}원`
            + (rz !== 0 ? `<br/>실현손익 <b>${rz.toLocaleString()}원</b>` : '');
        },
      },
      plotOptions: {
        series: { animation: false, marker: { enabled: true } },
        // scatter 가 점끼리 선으로 이어지던 것을 막는다 (2026-10-11 실측:
        // 매수체결이 지그재그 선으로 그려져 가격선과 구분이 안 됐다).
        scatter: { lineWidth: 0, linkedTo: undefined, states: { hover: { lineWidth: 0 } } },
      },
      series: [
        // 종목별 가격선을 먼저(아래) 깔고 체결 표식을 그 위에 올린다
        ...symSeries,
        {
          // 매수 = 초록 ▲, 매도 = 주황 ▼ — 차트 표식과 같은 색 규칙 (2026-10-04 요청:
          // 빨강·파랑은 캔들과 겹쳐 쓰지 않는다)
          // ⚠ scatter 에도 lineWidth 를 **명시적으로 0** 으로 둬야 한다.
          //   plotOptions.scatter 에만 걸면 안 먹는다 — 실측(2026-10-11)에서
          //   매수체결 시리즈에 highcharts-graph path(stroke #059669)가 그려져
          //   점들이 지그재그 선으로 이어졌다. 범례도 선으로 나왔다.
          type: 'scatter', name: '매수체결', data: buy, yAxis: 0,
          color: '#059669', lineWidth: 0,
          marker: { enabled: true, symbol: 'triangle', radius: 6 },
        } as Highcharts.SeriesScatterOptions,
        {
          type: 'scatter', name: '매도체결', data: sell, yAxis: 0,
          color: '#ea580c', lineWidth: 0,
          marker: { enabled: true, symbol: 'triangle-down', radius: 6 },
        } as Highcharts.SeriesScatterOptions,
        {
          type: 'line', name: '누적손익', data: cum, yAxis: 1,
          color: '#2563eb', lineWidth: 2, marker: { enabled: false },
        } as Highcharts.SeriesLineOptions,
      ],
    };
  }, [fills]);

  const selDaily = daily.find((x) => x.d === selD);
  // 종목이 2개 이상이면 차트를 등락률로 그린다 (제목 안내용)
  const chartUsePct = new Set(fills.map((f) => f.prodId)).size > 1;

  return (
    <div style={{ padding: 12, boxSizing: 'border-box' }}>

      {/* 조회 조건 */}
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={gridTitle}>체결 현황분석</h3>
          <span style={{ fontSize: 12, color: '#64748b' }}>기간:</span>
          <DateInput value={d0} onChange={(v: string) => setD0(v)} />
          <span style={{ color: '#9ca3af' }}>~</span>
          <DateInput value={d1} onChange={(v: string) => setD1(v)} />

          <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>계좌:</span>
          <select
            value={pfId ?? 0}
            onChange={(e) => setPfId(Number(e.target.value) || null)}
            style={{
              padding: '3px 8px', fontSize: 12, border: '1px solid #93c5fd',
              borderRadius: 4, color: '#1d4ed8', background: '#fff',
            }}
          >
            <option value={0}>전체</option>
            {portfolios.map((a: any) => (
              <option key={a.id} value={a.id}>{a.id} {a.idNm || a.htsId} {a.accountNo}</option>
            ))}
          </select>

          <button
            onClick={fetchAll}
            style={{
              padding: '4px 16px', fontSize: 12, fontWeight: 600, border: 'none',
              borderRadius: 4, background: '#2563eb', color: '#fff', cursor: 'pointer',
            }}
          >
            조회
          </button>
          {loading && <span style={{ fontSize: 12, color: '#9ca3af' }}>조회 중…</span>}
        </div>
      </div>

      {/* 1) 일자별 체결현황 */}
      <div style={card}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            일자별 체결현황 <span style={gridHint}>(일자 클릭: 아래 시간대·종목·차트가 그날로 바뀐다)</span>
          </h3>
          <span style={{ fontSize: 12, color: '#1e40af', fontWeight: 500 }}>총 {daily.length}일</span>
        </div>
        <div className="ag-theme-quartz" style={{ height: Math.min(300, Math.max(110, 50 + daily.length * 32)), width: '100%', fontSize: 12 }}>
          <AgGridReact
            rowData={daily}
            columnDefs={dailyCols}
            animateRows={false}
            headerHeight={36}
            rowHeight={32}
            overlayNoRowsTemplate="해당 기간 체결이 없습니다"
            onRowClicked={(e) => { if (e.data) fetchDay(e.data.d, pfId); }}
            getRowStyle={(p) => (p.data?.d === selD
              ? { backgroundColor: '#dbeafe', fontWeight: 600, cursor: 'pointer' }
              : { cursor: 'pointer' })}
          />
        </div>
      </div>

      {selD && (
        <>
          {/* 2) 시간대별 → 종목별 — **한 개씩 세로로** (2026-10-11 요청).
              나란히 두면 컬럼이 잘려 가로 스크롤을 해야 했다. */}
          <div>
            <div style={card}>
              <div style={titleRow}>
                <h3 style={gridTitle}>
                  시간대별 체결 <span style={gridHint}>
                    ({selD} · 30분 단위 · 현금흐름과 실현손익은 다르다)
                  </span>
                </h3>
                {sums && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: sign(sums.net) }}>
                    그날 순손익 {Math.round(sums.net).toLocaleString()}원
                    <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                      {' '}(실현 {Math.round(sums.realized).toLocaleString()}
                      {' '}+ 평가 {Math.round(sums.unrealized).toLocaleString()}
                      {' '}− 비용 {Math.round(sums.cost).toLocaleString()})
                      {costFromKis ? ' · 비용은 KIS 하루합계 안분' : ''}
                    </span>
                  </span>
                )}
              </div>
              <div className="ag-theme-quartz" style={{ height: Math.min(320, Math.max(110, 50 + byTime.length * 32)), width: '100%', fontSize: 12 }}>
                <AgGridReact
                  rowData={byTime}
                  columnDefs={timeCols}
                  animateRows={false}
                  headerHeight={36}
                  rowHeight={32}
                  overlayNoRowsTemplate="체결 없음"
                />
              </div>
            </div>

            <div style={card}>
              <div style={titleRow}>
                <h3 style={gridTitle}>
                  종목별 손익 <span style={gridHint}>({selD})</span>
                </h3>
                <span style={{ fontSize: 12, fontWeight: 600, color: sign(sums?.net ?? 0) }}>
                  순손익 합계 {Math.round(sums?.net ?? 0).toLocaleString()}원
                  <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                    {' '}(실현 {Math.round(sums?.realized ?? 0).toLocaleString()}
                    {' '}+ 평가 {Math.round(sums?.unrealized ?? 0).toLocaleString()}
                    {' '}− 비용 {Math.round(sums?.cost ?? 0).toLocaleString()}) · {bySym.length}종목
                  </span>
                  {sums && (
                    <span style={{
                      marginLeft: 8, fontWeight: 600,
                      color: Math.abs(sums.unrealGap) < 1 ? '#16a34a' : '#b45309',
                    }}>
                      {Math.abs(sums.unrealGap) < 1
                        ? '· KIS 평가손익과 일치 ✓'
                        : `· KIS 와 ${Math.round(sums.unrealGap).toLocaleString()} 차이`}
                    </span>
                  )}
                </span>
              </div>
              <div className="ag-theme-quartz" style={{ height: Math.min(320, Math.max(110, 50 + bySym.length * 32)), width: '100%', fontSize: 12 }}>
                <AgGridReact
                  rowData={bySym}
                  columnDefs={symCols}
                  animateRows={false}
                  headerHeight={36}
                  rowHeight={32}
                  overlayNoRowsTemplate="체결 없음"
                />
              </div>
            </div>
          </div>

          {/* 3) 분봉 차트 — 체결을 **실제 분봉 위에** 찍는다 (2026-10-11 요청).
              아래 '체결 차트'(체결 순서축)와 역할이 다르다:
                분봉차트 = 그 종목이 그날 어떻게 움직였고 어디서 사고팔았나
                체결차트 = 체결 순서대로 본 누적손익 흐름 */}
          <div style={card}>
            <div style={titleRow}>
              <h3 style={gridTitle}>
                분봉 차트 <span style={gridHint}>
                  ({selD} · ▲매수 ▼매도 체결을 분봉 위에 표시)
                </span>
              </h3>
              {bySym.length > 0 && (
                <select
                  value={chartSym ?? ''}
                  onChange={(e) => setChartSym(e.target.value || null)}
                  style={{
                    padding: '3px 8px', fontSize: 12, border: '1px solid #93c5fd',
                    borderRadius: 4, color: '#1d4ed8', background: '#fff', cursor: 'pointer',
                  }}
                >
                  {bySym.map((x) => (
                    <option key={x.prodId} value={x.prodId}>
                      {x.prodNm ?? x.prodId} ({x.prodId}) · {Math.round(x.pnl).toLocaleString()}원
                    </option>
                  ))}
                </select>
              )}
            </div>
            {chartSym ? (
              <StockChart
                // 종목이 바뀌면 새로 만든다 — 인스턴스를 재사용하면 내부 기간·데이터가 남는다
                key={`${chartSym}-${selD}`}
                prodId={chartSym}
                prodNm={bySym.find((x) => x.prodId === chartSym)?.prodNm}
                date={selD}
                height={460}
                markers={candleMarkers}
              />
            ) : (
              <div style={{ fontSize: 12, color: '#9ca3af', padding: '20px 0' }}>
                표시할 종목이 없습니다.
              </div>
            )}
          </div>

          {/* 4) 체결 차트 */}
          <div style={card}>
            <div style={titleRow}>
              <h3 style={gridTitle}>
                체결 차트 <span style={gridHint}>
                  ({selD} · ▲매수 ▼매도 · 파란선=누적손익 · x축은 체결 순서
                  {chartUsePct ? ' · 종목 가격대가 달라 등락률(%)로 환산' : ''})
                </span>
              </h3>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>총 {fills.length}건</span>
            </div>
            {fills.length > 0
              ? <HighchartsReact highcharts={Highcharts} options={chartOptions} />
              : <div style={{ fontSize: 12, color: '#9ca3af', padding: '20px 0' }}>체결이 없습니다.</div>}
          </div>
        </>
      )}
    </div>
  );
};

export default FillAnalysis;
